/**
 * Profile questions are stored as choice indexes (legacy Symfony ChoiceType
 * with a plain array: value = position in the list). The counts below are the
 * lengths of the legacy lists in Zm\GlobalBundle\Form\NodeHumanInfoPublicType /
 * NodeHumanInfoPrivateType; labels live in i18n under `profile.<field>.<index>`
 * and `profile.privateFields.<field>.values.<index>`, in the SAME order.
 */
export const PUBLIC_FIELD_OPTION_COUNTS = {
  studyLevel: 6,
  nodeBusiness: 19,
  maritalStatus: 4,
  children: 6,
  childrenCare: 3,
  livingPlace: 4,
  administrativeState: 7,
  familyValues: 5,
  peerPast: 3,
  faith: 2,
  minaj: 9,
  veil: 4,
  smoke: 3,
  studyCountry: 3,
  shape: 5,
  eye: 6,
  hair: 8,
  spokenLanguages: 11,
  lifePersonality: 27,
  boolean: 2,
} as const;

export type PublicOptionField = keyof typeof PUBLIC_FIELD_OPTION_COUNTS;

/** Legacy size list: 100..229 cm. */
export const SIZE_MIN = 100;
export const SIZE_MAX = 229;

export interface FieldOption {
  value: string;
  label: string;
}

// Only the key is used, so Tolgee's overloaded `t` is assignable.
type Translate = (key: string) => string;

export function getFieldOptions(t: Translate, field: PublicOptionField): FieldOption[] {
  const options: FieldOption[] = [];
  for (let i = 0; i < PUBLIC_FIELD_OPTION_COUNTS[field]; i++) {
    options.push({ value: String(i), label: t(`profile.${field}.${i}`) });
  }
  return options;
}

/** Legacy `nodeavatar` table: id ↔ file. */
export const AVATARS_MEN: { id: number; file: string }[] = Array.from({ length: 13 }, (_, i) => ({
  id: 10 + i,
  file: `avatar_men_${i + 1}.jpg`,
}));
export const AVATARS_WOMEN: { id: number; file: string }[] = [
  ...Array.from({ length: 9 }, (_, i) => ({ id: 1 + i, file: `avatar_women_${i + 1}.jpg` })),
  ...Array.from({ length: 5 }, (_, i) => ({ id: 23 + i, file: `avatar_women_${i + 10}.jpg` })),
];

export type PrivateFieldKind = 'single' | 'multi' | 'text';

export interface PrivateFieldDef {
  name: string;
  kind: PrivateFieldKind;
  /** number of choices (single/multi) */
  count?: number;
  /** legacy form: NotBlank / required=true */
  required: boolean;
  /** only asked to this gender (1 = men, 2 = women) */
  gender?: 1 | 2;
}

/** Legacy NodeHumanInfoPrivateType (signup step 8), in form order. */
export const PRIVATE_FIELDS: PrivateFieldDef[] = [
  { name: 'defect', kind: 'text', required: true },
  { name: 'makeSalatAtMasjid', kind: 'single', count: 5, required: true },
  { name: 'fajr', kind: 'single', count: 5, required: true },
  { name: 'personalityA', kind: 'single', count: 4, required: true },
  { name: 'personalityB', kind: 'single', count: 4, required: true },
  { name: 'personalityC', kind: 'single', count: 4, required: true },
  { name: 'personalityD', kind: 'single', count: 4, required: true },
  { name: 'personalityE', kind: 'single', count: 4, required: true },
  { name: 'personalityF', kind: 'single', count: 4, required: true },
  { name: 'personalityG', kind: 'single', count: 4, required: true },
  { name: 'personalityH', kind: 'single', count: 4, required: true },
  { name: 'sports', kind: 'multi', count: 21, required: false },
  { name: 'lifeStyle', kind: 'multi', count: 13, required: false },
  { name: 'outOrAtHouse', kind: 'single', count: 2, required: true },
  { name: 'tvPref', kind: 'multi', count: 6, required: false },
  { name: 'isTraveler', kind: 'single', count: 2, required: true },
  { name: 'travelDestination', kind: 'single', count: 4, required: true },
  { name: 'whoManageFinance', kind: 'single', count: 2, required: true },
  { name: 'whoFinanceWhat', kind: 'single', count: 3, required: true },
  { name: 'ribaWanted', kind: 'single', count: 4, required: true },
  { name: 'zawajReady', kind: 'single', count: 6, required: true },
  { name: 'childrenWanted', kind: 'single', count: 3, required: true },
  { name: 'familySize', kind: 'single', count: 3, required: false },
  { name: 'stepFamilyRelation', kind: 'single', count: 5, required: true },
  { name: 'maritalduty', kind: 'single', count: 4, required: true },
  { name: 'wantToLiveFar', kind: 'single', count: 5, required: true },
  { name: 'wantCivilMariage', kind: 'single', count: 2, required: true },
  { name: 'mariageOrganisation', kind: 'single', count: 4, required: true },
  { name: 'workingWomen', kind: 'single', count: 4, required: true, gender: 1 },
  { name: 'activityAfterWedding', kind: 'single', count: 3, required: true, gender: 2 },
];

export function privateFieldsForGender(gender: number | undefined): PrivateFieldDef[] {
  return PRIVATE_FIELDS.filter((f) => !f.gender || f.gender === gender);
}

export function getPrivateFieldOptions(t: Translate, field: PrivateFieldDef): FieldOption[] {
  const options: FieldOption[] = [];
  for (let i = 0; i < (field.count ?? 0); i++) {
    options.push({ value: String(i), label: t(`profile.privateFields.${field.name}.values.${i}`) });
  }
  return options;
}

/** Value of a private field as kept in forms: index string, list of index strings, or text. */
export type PrivateFieldValue = string | string[];
export type PrivateFormData = Record<string, PrivateFieldValue | undefined>;

export function isPrivateFieldFilled(value: PrivateFieldValue | undefined): boolean {
  if (value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  return value.trim().length > 0;
}

/** Converts form values to the PUT /v1/user/profile/private payload. */
export function toPrivatePayload(
  fields: PrivateFieldDef[],
  data: PrivateFormData,
): Record<string, string | number | number[]> {
  const payload: Record<string, string | number | number[]> = {};
  for (const field of fields) {
    const value = data[field.name];
    if (value === undefined) continue;
    if (field.kind === 'text') {
      if (typeof value === 'string') payload[field.name] = value;
    } else if (field.kind === 'multi') {
      payload[field.name] = (Array.isArray(value) ? value : [value]).map((v) => Number(v));
    } else if (typeof value === 'string' && value !== '') {
      payload[field.name] = Number(value);
    }
  }
  return payload;
}

/** Builds form values from the API's private field rows (one row per value). */
export function fromPrivateFieldRows(
  fields: PrivateFieldDef[],
  rows: { name: string; value: string }[],
): PrivateFormData {
  const data: PrivateFormData = {};
  for (const field of fields) {
    const values = rows.filter((r) => r.name === field.name).map((r) => r.value);
    if (values.length === 0) continue;
    data[field.name] = field.kind === 'multi' ? values : values[0];
  }
  return data;
}
